import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as XLSX from "xlsx";
import { uploadExcelData } from "../../../api/excel.api";
import { ILeadData } from "../../../models/LeadData.model";
import { reverseGeocode } from "../../../Services/reverseGeocoding";

const PAGE_SIZE = 10;
const CHUNK_SIZE = 100;
let uploadAbortController: AbortController | null = null;

interface ExcelState {
  excelData: ILeadData[];
  parsedData: ILeadData[];
  currentPage: number;
  fileUploaded: boolean;
  loading: boolean;
  uploading: boolean;
  uploadProgress: number;
  message: string;
  totalPages: number;
  currentPageData: ILeadData[];
  failedLeads: Array<{ row?: number | null; failedLead: any; reason: string }>;
}

const initialState: ExcelState = {
  excelData: [],
  parsedData: [],
  currentPage: 1,
  fileUploaded: false,
  loading: false,
  uploading: false,
  uploadProgress: 0,
  message: "",
  totalPages: 0,
  currentPageData: [],
  failedLeads: [],
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const transformScrapperRow = async (row: any) => {
  const get = (k: string) =>
    row[k] !== undefined ? String(row[k]).trim() : "";
  const name = get("Name") || get("name");
  const mobileNumber =
    get("Mobile Number") || get("Mobile") || get("mobileNumber") || "";
  let addressLine1 = get("Address") || get("address") || "";
  const email = get("Email Id") || get("Email") || get("email") || "";

  const latitude = parseFloat(get("latitude") || get("lat") || "");
  const longitude = parseFloat(
    get("longitude") || get("long") || get("lng") || "",
  );

  let addressdistrict = get("Location") || get("location") || "";
  const stateRaw = get("State") || get("state") || "";

  // extract 6-digit pincode if present
  const pincodeMatch = stateRaw.match(/\b\d{6}\b/);
  let addressPincode = pincodeMatch ? pincodeMatch[0] : "";
  let addressState = pincodeMatch
    ? stateRaw.replace(pincodeMatch[0], "").trim()
    : stateRaw;

  addressdistrict = addressdistrict.toLowerCase();
  addressState = addressState.toLowerCase();

  // If state or district is missing, try to geocode
  if (
    (!addressState || !addressdistrict) &&
    !isNaN(latitude) &&
    !isNaN(longitude)
  ) {
    try {
      const geocodeResult = await reverseGeocode(latitude, longitude);
      await delay(1000); // Respect Nominatim's rate limit of 1 request per second

      if (geocodeResult) {
        if (!addressState) {
          addressState = geocodeResult.address.state?.toLowerCase() || "";
        }

        if (!addressdistrict) {
          addressdistrict = (
            geocodeResult.address.district || ""
          ).toLowerCase();
        }

        if (!addressPincode) {
          addressPincode = geocodeResult.address.postcode || "";
        }

        if (!addressLine1) {
          addressLine1 = geocodeResult.display_name || "";
        }
      }
    } catch (error) {
      console.error(
        "Geocoding failed for coordinates:",
        latitude,
        longitude,
        error,
      );
    }
  }

  const transformed: any = {
    name,
    mobileNumber,
    category: "Epc",
    addressLine1,
    email,
    addressdistrict,
    addressState,
    addressPincode,
  };

  return transformed as ILeadData;
};

export const processExcelFile = createAsyncThunk<
  ILeadData[],
  { file: File; isScrapper?: boolean },
  { rejectValue: string }
>("excel/processExcelFile", async ({ file, isScrapper = false }) => {
  return new Promise<ILeadData[]>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (evt: ProgressEvent<FileReader>) => {
      try {
        if (evt.target?.result) {
          const data = new Uint8Array(evt.target.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          let allData: ILeadData[] = [];

          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json<ILeadData>(worksheet, {
              defval: "",
            });
            allData = [...allData, ...jsonData];
          });
          if (isScrapper) {
            const transformedData: ILeadData[] = [];
            for (const row of allData) {
              const transformedRow = await transformScrapperRow(row);
              transformedData.push(transformedRow);
            }
            resolve(transformedData);
          } else {
            resolve(allData as ILeadData[]);
          }
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
});

// Async thunk for uploading Excel data in chunks
export const uploadExcelFileInChunks = createAsyncThunk(
  "excel/uploadExcelFileInChunks",
  async (_, { getState, dispatch, rejectWithValue, signal }) => {
    const state = getState() as { excel: ExcelState };
    const { parsedData } = state.excel;

    if (parsedData.length === 0) {
      return rejectWithValue("No data to upload");
    }

    uploadAbortController = new AbortController();
    signal.addEventListener("abort", () => {
      uploadAbortController?.abort();
    });

    const totalChunks = Math.ceil(parsedData.length / CHUNK_SIZE);
    let successCount = 0;
    const errors: string[] = [];

    dispatch(setUploading(true));
    dispatch(setUploadProgress(0));

    try {
      const collectedFailedLeads: Array<any> = [];

      for (let i = 0; i < totalChunks; i++) {
        // Check if the upload was aborted
        if (uploadAbortController.signal.aborted) {
          throw new Error("Upload cancelled");
        }

        const chunk = parsedData.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

        try {
          const resp = await uploadExcelData(chunk, {
            signal: uploadAbortController.signal,
          });
          if (resp?.data?.failedLeads && Array.isArray(resp.data.failedLeads)) {
            collectedFailedLeads.push(...resp.data.failedLeads);
          }
          successCount++;
          const progress = Math.round(((i + 1) / totalChunks) * 100);
          dispatch(setUploadProgress(progress));
          await delay(200);
        } catch (error: any) {
          if (error.name === "AbortError") {
            throw error;
          }
          const errorMessage =
            error.response?.data?.message || `Chunk ${i + 1} failed`;
          errors.push(errorMessage);
          console.error(`Chunk ${i + 1} failed:`, error);
        }
      }

      if (errors.length > 0 && successCount === 0) {
        throw new Error(`All chunks failed. Errors: ${errors.join(", ")}`);
      }

      return {
        successCount,
        totalChunks,
        errors,
        message:
          successCount === totalChunks
            ? "All data uploaded successfully!"
            : `${successCount}/${totalChunks} chunks uploaded successfully. Some chunks failed.`,
        failedLeads: collectedFailedLeads,
      };
    } finally {
      uploadAbortController = null;
    }
  },
);

export const cancelExcelUpload = createAsyncThunk(
  "excel/cancelUpload",
  async (_, { dispatch }) => {
    if (uploadAbortController) {
      uploadAbortController.abort();
    }
    dispatch(resetUploadState());
    return "Upload cancelled";
  },
);

// Original upload function (keeping for backward compatibility)
export const uploadExcelFile = createAsyncThunk(
  "excel/uploadExcelFile",
  async (data: ILeadData[], { rejectWithValue }) => {
    try {
      const response = await uploadExcelData(data);
      return response.data;
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(
        "An unexpected error occurred while uploading the Excel file.",
      );
    }
  },
);

const excelSlice = createSlice({
  name: "excel",
  initialState,
  reducers: {
    setExcelData(state, action: PayloadAction<ILeadData[]>) {
      state.excelData = action.payload;
    },
    setParsedData(state, action: PayloadAction<ILeadData[]>) {
      state.parsedData = action.payload;
      state.totalPages = Math.ceil(action.payload.length / PAGE_SIZE);
      state.fileUploaded = action.payload.length > 0;
      // Update current page data
      const startIdx = (state.currentPage - 1) * PAGE_SIZE;
      state.currentPageData = action.payload.slice(
        startIdx,
        startIdx + PAGE_SIZE,
      );
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      const newPage = action.payload;
      if (newPage >= 1 && newPage <= state.totalPages) {
        state.currentPage = newPage;
        // Update current page data
        const startIdx = (newPage - 1) * PAGE_SIZE;
        state.currentPageData = state.parsedData.slice(
          startIdx,
          startIdx + PAGE_SIZE,
        );
      }
    },
    setMessage(state, action: PayloadAction<string>) {
      state.message = action.payload;
    },
    setUploading(state, action: PayloadAction<boolean>) {
      state.uploading = action.payload;
    },
    setUploadProgress(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload;
    },
    setFileUploaded(state, action: PayloadAction<boolean>) {
      state.fileUploaded = action.payload;
    },
    goToNextPage(state) {
      if (state.currentPage < state.totalPages) {
        state.currentPage += 1;
        const startIdx = (state.currentPage - 1) * PAGE_SIZE;
        state.currentPageData = state.parsedData.slice(
          startIdx,
          startIdx + PAGE_SIZE,
        );
      }
    },
    goToPreviousPage(state) {
      if (state.currentPage > 1) {
        state.currentPage -= 1;
        const startIdx = (state.currentPage - 1) * PAGE_SIZE;
        state.currentPageData = state.parsedData.slice(
          startIdx,
          startIdx + PAGE_SIZE,
        );
      }
    },
    resetState(state) {
      Object.assign(state, initialState);
    },
    resetUploadState(state) {
      state.parsedData = [];
      state.fileUploaded = false;
      state.currentPage = 1;
      state.totalPages = 0;
      state.currentPageData = [];
      state.uploading = false;
      state.uploadProgress = 0;
    },
    setFailedLeads(state, action: PayloadAction<ExcelState["failedLeads"]>) {
      state.failedLeads = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Process Excel File
      .addCase(processExcelFile.pending, (state) => {
        state.loading = true;
        state.message = "";
      })
      .addCase(processExcelFile.fulfilled, (state, action) => {
        state.loading = false;
        state.parsedData = action.payload;
        state.totalPages = Math.ceil(action.payload.length / PAGE_SIZE);
        state.fileUploaded = action.payload.length > 0;
        state.currentPage = 1;
        // Set current page data
        state.currentPageData = action.payload.slice(0, PAGE_SIZE);
        state.message = `Successfully parsed ${action.payload.length} records`;
      })
      .addCase(processExcelFile.rejected, (state, action) => {
        state.loading = false;
        state.message = action.error.message || "Failed to process Excel file";
      })

      // Upload Excel File in Chunks
      .addCase(uploadExcelFileInChunks.pending, (state) => {
        state.loading = true;
        state.uploading = true;
        state.uploadProgress = 0;
        state.message = "";
      })
      .addCase(uploadExcelFileInChunks.fulfilled, (state, action) => {
        state.loading = false;
        state.uploading = false;
        state.uploadProgress = 100;
        state.message = action.payload.message;
        // Reset upload-related state after successful upload
        state.parsedData = [];
        state.fileUploaded = false;
        state.currentPage = 1;
        state.totalPages = 0;
        state.currentPageData = [];
        state.failedLeads = action.payload.failedLeads || [];
      })
      .addCase(uploadExcelFileInChunks.rejected, (state, action) => {
        state.loading = false;
        state.uploading = false;
        if (action.error.message?.includes("cancelled")) {
          state.message = "Upload cancelled";
        } else {
          state.message = action.error.message || "Failed to upload Excel data";
        }
      })

      // Original Upload Excel File (keeping for backward compatibility)
      .addCase(uploadExcelFile.pending, (state) => {
        state.loading = true;
        state.message = "";
      })
      .addCase(uploadExcelFile.fulfilled, (state, action) => {
        state.loading = false;
        state.message = "Leads created successfully!";
        state.excelData = action.payload;
      })
      .addCase(uploadExcelFile.rejected, (state, action) => {
        state.loading = false;
        state.message =
          (action.payload as string) ||
          "An error occurred while uploading the Excel file.";
      })
      .addCase(cancelExcelUpload.pending, (state) => {
        state.uploading = false;
        state.loading = false;
        state.message = "Cancelling upload...";
      })
      .addCase(cancelExcelUpload.fulfilled, (state, action) => {
        state.message = action.payload;
        state.uploadProgress = 0;
      })
      .addCase(cancelExcelUpload.rejected, (state, action) => {
        state.message = action.error.message || "Failed to cancel upload";
      });
  },
});

export const {
  setExcelData,
  setParsedData,
  setCurrentPage,
  setMessage,
  setUploading,
  setUploadProgress,
  setFileUploaded,
  goToNextPage,
  goToPreviousPage,
  resetState,
  resetUploadState,
} = excelSlice.actions;

export default excelSlice.reducer;
